<template>
  <div :class="['form-group', { 'is-filter-active': modelValue !== '' && modelValue !== null && modelValue !== undefined }]">
    <label v-if="label" :for="id">
      {{ label }}
      <span v-if="required" class="required-indicator" aria-label="requis">*</span>
      <Tooltip v-if="modelValue !== '' && modelValue !== null && modelValue !== undefined" content="Réinitialiser ce filtre" placement="top">
        <i
          class="fas fa-undo filter-clear-icon"
          aria-label="Réinitialiser ce filtre"
          role="button"
          tabindex="-1"
          @click.stop.prevent="handleClear"
        ></i>
      </Tooltip>
    </label>
    <div
      v-if="variant === 'status'"
      class="select-wrapper select-status-wrapper"
    >
      <span
        class="status-dot"
        :style="statusDotStyle"
      ></span>
      <select
        :id="id"
        :value="modelValue"
        :disabled="disabled"
        :required="required"
        :aria-required="required"
      :aria-label="ariaLabel || undefined"
        :name="name"
        :data-testid="testId"
        :class="['form-control', `select-${variant}`, { 'form-control-error': error }]"
        @change="$emit('update:modelValue', $event.target.value)"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      >
        <option 
          v-for="option in normalizedOptions" 
          :key="getOptionValue(option)" 
          :value="getOptionValue(option)"
          :disabled="option?.disabled"
        >
          {{ getOptionLabel(option) }}
        </option>
        <slot></slot>
      </select>
    </div>
    <select
      v-else
      :id="id"
      :value="modelValue"
      :disabled="disabled"
      :required="required"
      :aria-required="required"
      :aria-label="ariaLabel || undefined"
      :name="name"
      :data-testid="testId"
      :class="['form-control', `select-${variant}`, { 'form-control-error': error }]"
      @change="$emit('update:modelValue', $event.target.value)"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    >
      <option 
        v-for="option in normalizedOptions" 
        :key="getOptionValue(option)" 
        :value="getOptionValue(option)"
        :disabled="option?.disabled"
      >
        {{ getOptionLabel(option) }}
      </option>
      <slot></slot>
    </select>
    <span v-if="error" class="error-text">{{ error }}</span>
    <span v-if="hint && !error" class="hint-text">{{ hint }}</span>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Tooltip from './Tooltip.vue'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  label: {
    type: String,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  // Portée sur le <select> natif : c'est ce qui rend le champ visible de
  // `form.checkValidity()`. Le contrôle ne bloque que si la valeur courante
  // correspond à une option vide — pensez au placeholder `{ value: '' }`.
  required: {
    type: Boolean,
    default: false
  },
  // Nom du champ, repris par le formulaire pour nommer le champ fautif.
  name: {
    type: String,
    default: null
  },
  error: {
    type: String,
    default: null
  },
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'status', 'assign'].includes(value)
  },
  id: {
    type: String,
    default: () => `select-${Math.random().toString(36).substr(2, 9)}`
  },
  testId: {
    type: String,
    default: null
  },
  /**
   * Nom accessible du `<select>` natif quand le champ n'a pas de libellé
   * visible (ex. heures « de / à » d'un créneau). Sans cette prop, `aria-label`
   * tombait sur la `div.form-group` racine et le champ restait sans nom.
   */
  ariaLabel: {
    type: String,
    default: null
  },
  /**
   * Texte d'aide sous le champ, comme sur Input et Textarea. Faute de cette
   * prop, chaque fenêtre qui devait expliquer un choix reposait un `<p>` et son
   * style local — la divergence exacte que le kit existe pour éviter.
   * Masqué quand une erreur est affichée : les deux ne se lisent pas ensemble.
   */
  hint: {
    type: String,
    default: null
  },
  options: {
    type: Array,
    default: () => []
  },
  statusConfig: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus'])

const statusDotStyle = computed(() => {
  const cfg = props.statusConfig[props.modelValue]
  if (cfg) return { backgroundColor: cfg.border || cfg.color }
  return { backgroundColor: 'var(--color-border)' }
})

const handleClear = () => {
  emit('update:modelValue', '')
}

const normalizedOptions = computed(() => {
  if (!props.options || props.options.length === 0) {
    return []
  }
  return props.options.map(option => {
    if (typeof option === 'string') {
      return { value: option, label: option }
    }
    return option
  })
})

function getOptionValue(option) {
  return option?.value ?? option
}

function getOptionLabel(option) {
  return option?.label ?? option?.value ?? option
}
</script>

<style scoped>
/* Status select variants */
.select-status-wrapper {
  position: relative;
  display: inline-block;
}

.status-dot {
  position: absolute;
  left: var(--spacing-sm);
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  z-index: 1;
  pointer-events: none;
}


.select-status {
  width: auto;
  min-height: 34px;
  padding: 0.35rem 0.6rem;
  padding-left: calc(var(--spacing-sm) + 12px);
  font-size: 0.85rem;
}

.select-assign {
  width: auto;
  min-height: 34px;
  padding: 0.35rem 0.6rem;
  font-size: 0.85rem;
  min-width: 150px;
}

.filter-clear-icon {
  display: none;
  margin-left: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  color: var(--color-primary);
  transition: color var(--transition-base);
  vertical-align: middle;
}

label :deep(.tooltip-wrapper) {
  display: inline-flex;
  width: auto;
}

.filter-clear-icon:hover {
  color: var(--color-danger);
}
</style>

